import { useState, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, IconButton, Tooltip, Slider } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: '#ffffff',
    borderLeft: '1px solid #e0e0e0',
  },
  header: {
    padding: '14px 16px 10px',
    borderBottom: '1px solid #f0f0f0',
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: '#333',
    marginBottom: 2,
  },
  itemName: {
    fontSize: 12,
    color: '#666',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  summary: {
    fontSize: 12,
    color: '#555',
    padding: '8px 16px',
    background: '#f9fafb',
    borderBottom: '1px solid #f0f0f0',
  },
  thresholdSection: {
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0',
  },
  thresholdLabel: {
    fontSize: 11,
    color: '#888',
    marginBottom: 4,
  },
  slider: {
    color: '#7c3aed',
    padding: '8px 0',
  },
  thresholdValue: {
    fontSize: 12,
    fontWeight: 600,
    color: '#7c3aed',
    textAlign: 'center',
    marginTop: 2,
  },
  bulkActions: {
    display: 'flex',
    gap: 8,
    padding: '10px 16px',
    borderBottom: '1px solid #f0f0f0',
  },
  acceptAllBtn: {
    flex: 1,
    textTransform: 'none',
    fontSize: 11,
    fontWeight: 600,
    color: '#fff',
    background: '#4caf50',
    '&:hover': { background: '#388e3c' },
  },
  rejectAllBtn: {
    flex: 1,
    textTransform: 'none',
    fontSize: 11,
    fontWeight: 600,
    color: '#ef5350',
    borderColor: '#ef5350',
    '&:hover': { background: '#ffebee', borderColor: '#e53935' },
  },
  candidateList: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px 0',
  },
  candidateRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 16px',
    '&:hover': { background: '#f9fafb' },
  },
  confidenceBar: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    background: '#e0e0e0',
    position: 'relative',
    overflow: 'hidden',
  },
  confidenceFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    borderRadius: 3,
  },
  confidenceLabel: {
    fontSize: 11,
    fontWeight: 600,
    minWidth: 36,
    textAlign: 'right',
  },
  actionBtns: {
    display: 'flex',
    gap: 2,
  },
  acceptBtn: {
    padding: 3,
    color: '#4caf50',
  },
  rejectBtn: {
    padding: 3,
    color: '#ef5350',
  },
  statsSection: {
    padding: '10px 16px',
    borderTop: '1px solid #f0f0f0',
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
  },
  stat: {
    fontSize: 11,
    color: '#666',
    textAlign: 'center',
  },
  statValue: {
    fontSize: 14,
    fontWeight: 600,
    display: 'block',
  },
  footer: {
    padding: '12px 16px',
    borderTop: '1px solid #e0e0e0',
  },
  doneBtn: {
    width: '100%',
    textTransform: 'none',
    fontSize: 12,
    fontWeight: 600,
  },
}));

/**
 * CandidateReviewPanel — sidebar panel for reviewing CV match candidates.
 */
const CandidateReviewPanel = ({
  candidates,
  itemName,
  onAcceptCandidate,
  onRejectCandidate,
  onAcceptAllAbove,
  onRejectAllBelow,
  onDone,
}) => {
  const classes = useStyles();
  const [threshold, setThreshold] = useState(0.85);

  const pendingCandidates = useMemo(
    () =>
      (candidates || [])
        .map((c, idx) => ({ ...c, _idx: idx }))
        .filter((c) => c.status === 'pending')
        .sort((a, b) => b.confidence - a.confidence),
    [candidates]
  );

  const highConfidenceCount = useMemo(
    () => (candidates || []).filter((c) => c.confidence >= 0.85).length,
    [candidates]
  );

  const stats = useMemo(() => {
    const all = candidates || [];
    return {
      accepted: all.filter((c) => c.status === 'accepted').length,
      rejected: all.filter((c) => c.status === 'rejected').length,
      pending: all.filter((c) => c.status === 'pending').length,
    };
  }, [candidates]);

  const getConfidenceColor = (conf) => {
    if (conf >= 0.85) return '#4caf50';
    if (conf >= 0.65) return '#ff9800';
    return '#ef5350';
  };

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <div className={classes.title}>CV Match Results</div>
        <div className={classes.itemName} title={itemName}>
          {itemName}
        </div>
      </div>

      {/* Summary */}
      <div className={classes.summary}>
        {(candidates || []).length} found ({highConfidenceCount} high confidence)
      </div>

      {/* Threshold slider */}
      <div className={classes.thresholdSection}>
        <div className={classes.thresholdLabel}>Accept threshold</div>
        <Slider
          className={classes.slider}
          value={threshold}
          onChange={(_, val) => setThreshold(val)}
          min={0.5}
          max={1.0}
          step={0.05}
          aria-labelledby="threshold-slider"
        />
        <div className={classes.thresholdValue}>{Math.round(threshold * 100)}%</div>
      </div>

      {/* Bulk actions */}
      <div className={classes.bulkActions}>
        <Button
          className={classes.acceptAllBtn}
          variant="contained"
          size="small"
          disableElevation
          onClick={() => onAcceptAllAbove && onAcceptAllAbove(threshold)}
        >
          Accept All &ge; {Math.round(threshold * 100)}%
        </Button>
        <Button
          className={classes.rejectAllBtn}
          variant="outlined"
          size="small"
          onClick={() => onRejectAllBelow && onRejectAllBelow(threshold)}
        >
          Reject All &lt; {Math.round(threshold * 100)}%
        </Button>
      </div>

      {/* Candidate list */}
      <div className={classes.candidateList}>
        {pendingCandidates.map((candidate) => (
          <div key={candidate._idx} className={classes.candidateRow}>
            <div className={classes.confidenceBar}>
              <div
                className={classes.confidenceFill}
                style={{
                  width: `${Math.round(candidate.confidence * 100)}%`,
                  background: getConfidenceColor(candidate.confidence),
                }}
              />
            </div>
            <span
              className={classes.confidenceLabel}
              style={{ color: getConfidenceColor(candidate.confidence) }}
            >
              {Math.round(candidate.confidence * 100)}%
            </span>
            <div className={classes.actionBtns}>
              <Tooltip title="Accept">
                <IconButton
                  className={classes.acceptBtn}
                  size="small"
                  onClick={() => onAcceptCandidate && onAcceptCandidate(candidate._idx)}
                >
                  <CheckIcon style={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject">
                <IconButton
                  className={classes.rejectBtn}
                  size="small"
                  onClick={() => onRejectCandidate && onRejectCandidate(candidate._idx)}
                >
                  <CloseIcon style={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        ))}
        {pendingCandidates.length === 0 && (
          <div style={{ textAlign: 'center', padding: 16, fontSize: 12, color: '#999' }}>
            No pending candidates
          </div>
        )}
      </div>

      {/* Stats */}
      <div className={classes.statsSection}>
        <div className={classes.stat}>
          <span className={classes.statValue} style={{ color: '#4caf50' }}>
            {stats.accepted}
          </span>
          accepted
        </div>
        <div className={classes.stat}>
          <span className={classes.statValue} style={{ color: '#ef5350' }}>
            {stats.rejected}
          </span>
          rejected
        </div>
        <div className={classes.stat}>
          <span className={classes.statValue} style={{ color: '#7c3aed' }}>
            {stats.pending}
          </span>
          pending
        </div>
      </div>

      {/* Done button */}
      <div className={classes.footer}>
        <Button
          className={classes.doneBtn}
          variant="outlined"
          color="primary"
          onClick={onDone}
        >
          Done Reviewing
        </Button>
      </div>
    </div>
  );
};

export default CandidateReviewPanel;
